/**
 * useFeatureFlags — server-authoritative feature entitlements.
 *
 * Fetches GET /api/user/features (the authoritative source) once after login
 * and exposes the entitled feature ids + a hasAccess() helper for gating the
 * sidebar and page routes.
 *
 * Safe defaults (never fail OPEN):
 *  - While loading, nothing extra is unlocked.
 *  - If the request fails, we fall back to the LOCAL entitlements map using the
 *    org type stored in localStorage (or the minimal 'other' core-only set).
 *    We never grant all features on error.
 */

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { FEATURE_ENTITLEMENTS } from '../utils/entitlements';

// Always-accessible pages, regardless of organization type.
const ALWAYS_ON = ['home', 'profile'];

export interface FeatureFlags {
  features: string[];
  orgType: string | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  hasAccess: (featureId: string) => boolean;
  refetch: () => void;
}

interface StoredProfile {
  organization_type?: string;
  is_admin?: boolean;
}

function readStoredProfile(): StoredProfile {
  try {
    const raw = localStorage.getItem('user_profile');
    return raw ? (JSON.parse(raw) as StoredProfile) : {};
  } catch {
    return {};
  }
}

/** Core-only fallback derived from the local entitlements map (fail-closed). */
function localFallback(orgType: string | null): string[] {
  const key = (orgType || 'other').toLowerCase();
  const base = FEATURE_ENTITLEMENTS[key] || FEATURE_ENTITLEMENTS['other'];
  return Array.from(new Set([...base, ...ALWAYS_ON]));
}

export function useFeatureFlags(isAuthenticated?: boolean): FeatureFlags {
  const initialProfile = readStoredProfile();
  // Optimistic (fail-closed) init from the local map so gating works before the
  // server responds — avoids a blank flash without ever unlocking everything.
  const [features, setFeatures] = useState<string[]>(() =>
    localFallback(initialProfile.organization_type || null)
  );
  const [orgType, setOrgType] = useState<string | null>(initialProfile.organization_type || null);
  const [isAdmin, setIsAdmin] = useState<boolean>(Boolean(initialProfile.is_admin));
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('access_token');
    const stored = readStoredProfile();
    const storedOrg = stored.organization_type || null;

    // Not authenticated: expose only always-on pages.
    if (!token) {
      setFeatures([...ALWAYS_ON]);
      setOrgType(storedOrg);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      // Retry transient failures (429 rate limit / network blips) so one bad
      // response doesn't leave this window stuck on the core-only nav.
      let res: Response | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          res = await fetch(`${API_BASE_URL}/api/user/features`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok || (res.status !== 429 && res.status < 500)) break;
        } catch (netErr) {
          if (attempt === 2) throw netErr;
          res = null;
        }
        if (attempt < 2) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
      if (!res || !res.ok) throw new Error(`features request failed: ${res ? res.status : 'network'}`);
      const data = await res.json();

      setFeatures(Array.isArray(data.features) ? data.features : localFallback(storedOrg));
      setOrgType(data.org_type ?? storedOrg);
      setIsAdmin(Boolean(data.is_admin));
    } catch (err) {
      // Fail CLOSED to the local core-only set — never unlock everything.
      console.warn('useFeatureFlags: falling back to local entitlements', err);
      setFeatures(localFallback(storedOrg));
      setOrgType(storedOrg);
      setIsAdmin(Boolean(stored.is_admin));
      setError(err instanceof Error ? err.message : 'Failed to load features');
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-run on login/logout: the hook mounts on the login screen (no token),
  // and without this dependency the post-login SPA transition never refetches.
  useEffect(() => {
    load();
  }, [load, isAuthenticated]);

  // If the last load failed, refetch when the window regains focus so a
  // window that hit a transient error recovers without a manual refresh.
  useEffect(() => {
    if (!error) return;
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [error, load]);

  const hasAccess = useCallback(
    (featureId: string) => {
      if (ALWAYS_ON.includes(featureId)) return true;
      if (isAdmin) return true;
      return features.includes(featureId);
    },
    [features, isAdmin]
  );

  return { features, orgType, isAdmin, loading, error, hasAccess, refetch: load };
}

export default useFeatureFlags;
